import { useState } from 'react'
import { Bot, ChevronDown, Sparkles, Settings2 } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'

export interface AgentNodeConfig {
  agentId: string
  agentName: string
  inputMapping: Record<string, string>
  outputVariable: string
  prompt?: string
  useConversationContext: boolean
  maxRetries: number
  timeoutSeconds: number
}

interface AgentNodeProps {
  config: AgentNodeConfig
  onChange: (config: AgentNodeConfig) => void
  availableAgents: Array<{ id: string; name: string; description?: string }>
  availableVariables: string[]
}

const defaultConfig: AgentNodeConfig = {
  agentId: '',
  agentName: '',
  inputMapping: {},
  outputVariable: 'agent_response',
  useConversationContext: false,
  maxRetries: 3,
  timeoutSeconds: 60,
}

export function AgentNode({ config = defaultConfig, onChange, availableAgents, availableVariables }: AgentNodeProps) {
  const [showAdvanced, setShowAdvanced] = useState(false)

  const handleAgentChange = (agentId: string) => {
    const agent = availableAgents.find(a => a.id === agentId)
    onChange({
      ...config,
      agentId,
      agentName: agent?.name || '',
    })
  }

  return (
    <div className="space-y-5">
      {/* Agent Selection */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Select Agent
        </Label>
        <Select value={config.agentId} onValueChange={handleAgentChange}>
          <SelectTrigger className="w-full rounded-xl">
            <SelectValue placeholder="Choose an agent..." />
          </SelectTrigger>
          <SelectContent className="rounded-xl">
            {availableAgents.map((agent) => (
              <SelectItem key={agent.id} value={agent.id} className="rounded-lg">
                <div className="flex items-center gap-2">
                  <Bot className="h-4 w-4 text-purple-500" />
                  <div>
                    <span className="font-medium">{agent.name}</span>
                    {agent.description && (
                      <span className="text-xs text-gray-500 ml-2">{agent.description}</span>
                    )}
                  </div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Prompt Override */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-500" />
            Custom Prompt (Optional)
          </div>
        </Label>
        <Textarea
          value={config.prompt || ''}
          onChange={(e) => onChange({ ...config, prompt: e.target.value })}
          placeholder="Override the agent's default prompt for this step..."
          className="min-h-[80px] rounded-xl resize-none"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Use {'{{variable_name}}'} to reference workflow variables
        </p>
      </div>

      {/* Output Variable */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Output Variable Name
        </Label>
        <input
          type="text"
          value={config.outputVariable}
          onChange={(e) => onChange({ ...config, outputVariable: e.target.value })}
          placeholder="agent_response"
          className="w-full h-11 px-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent transition-all"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400">
          This variable will contain the agent's response for use in subsequent steps
        </p>
      </div>

      {/* Input Mapping */}
      {availableVariables.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Input Variables
          </Label>
          <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Available from previous steps:
            </p>
            <div className="flex flex-wrap gap-1.5">
              {availableVariables.map((variable) => (
                <span
                  key={variable}
                  className="inline-flex px-2 py-1 rounded-lg text-xs font-mono bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                >
                  {`{{${variable}}}`}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Advanced Settings Toggle */}
      <button
        onClick={() => setShowAdvanced(!showAdvanced)}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
      >
        <Settings2 className="h-4 w-4" />
        Advanced Settings
        <ChevronDown className={`h-4 w-4 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
      </button>

      {/* Advanced Settings */}
      {showAdvanced && (
        <div className="space-y-4 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Use Conversation Context
              </Label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Include previous agent responses in context
              </p>
            </div>
            <Switch
              checked={config.useConversationContext}
              onCheckedChange={(checked) => onChange({ ...config, useConversationContext: checked })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Max Retries
              </Label>
              <input
                type="number"
                min={0}
                max={10}
                value={config.maxRetries}
                onChange={(e) => onChange({ ...config, maxRetries: parseInt(e.target.value) || 0 })}
                className="w-full h-11 px-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent transition-all"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Timeout (seconds)
              </Label>
              <input
                type="number"
                min={10}
                max={300}
                value={config.timeoutSeconds}
                onChange={(e) => onChange({ ...config, timeoutSeconds: parseInt(e.target.value) || 60 })}
                className="w-full h-11 px-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent transition-all"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export { defaultConfig as defaultAgentNodeConfig }
